/**
 * useHiTeachVersion.js
 *
 * 統一管理 HiTeach 版本判斷的 composable
 *
 * 功能：
 * - 讀取和管理 HiTeach 版本號
 * - 判斷是否支援新版 Push 功能（>= 5.5.0.0002）
 * - 提供版本比較工具函式
 */

import { ref, computed, onMounted, nextTick } from 'vue';

/**
 * HiTeach 版本管理 composable
 *
 * @param {string} componentName - 組件名稱（用於 debug log）
 * @returns {Object} - 包含版本相關的 ref 和 computed
 */
export const useHiTeachVersion = (componentName = 'Component') => {
  // HiTeach 版本號
  const htVersion = ref('');

  /**
   * 比較版本號是否 >= 指定版本
   * @param {string} currentVersion - 當前版本號 (e.g., "5.5.0.0002")
   * @param {string} targetVersion - 目標版本號 (e.g., "5.5.0.2")
   * @returns {boolean}
   */
  const compareVersion = (currentVersion, targetVersion) => {
    if (!currentVersion) return false;

    const current = currentVersion.split('.').map(Number);
    const target = targetVersion.split('.').map(Number);

    for (let i = 0; i < Math.max(current.length, target.length); i++) {
      const currentPart = current[i] || 0;
      const targetPart = target[i] || 0;

      if (currentPart > targetPart) return true;
      if (currentPart < targetPart) return false;
    }

    return true; // 版本相等
  };

  /**
   * 判斷是否支援新版 Push 功能
   * 需要 HiTeach >= 5.5.0.0002
   */
  const isNewPushFuncVersion = computed(() => {
    return compareVersion(htVersion.value, '5.5.0.2');
  });

  /**
   * 從 sessionStorage 載入 HiTeach 版本號
   */
  const loadVersion = () => {
    const savedVersion = sessionStorage.getItem('HiTeach_Version');
    if (savedVersion) {
      htVersion.value = savedVersion;
      nextTick(() => {
        console.log(
          `[${componentName}] HiTeach 版本:`,
          htVersion.value,
          '支援新 Push/名單引用 功能:',
          isNewPushFuncVersion.value
        );
      });
    }
  };

  /**
   * 自動載入版本（可選）
   * 如果需要在 onMounted 時自動載入，可以調用此函式
   */
  const autoLoadVersion = () => {
    onMounted(() => {
      loadVersion();
    });
  };

  return {
    htVersion,
    isNewPushFuncVersion,
    compareVersion,
    loadVersion,
    autoLoadVersion,
  };
};
